import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Typography, Tabs, Tab, Button, Stack } from "@mui/material";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useRoster } from "../../../contexts/RosterContext";
import { DepthChartComponent } from "./DepthChartComponent";

import { PlayerList } from "./PlayerList";
import { useDepthChart } from "../../../contexts/DepthChartContext";
import { useSeason } from "../../../contexts/SeasonContext";
import { useClub } from "../../../contexts/ClubContext";
// Use type-only import for html2canvas
import type html2canvas from "html2canvas";
import { ClubUser, ShareModal } from "./ShareModal";
import { transformRosterPlayersToList, isValidPositionDrop } from "./services";
import { SavedChartsList } from "./SavedChartsList";
import { usePrintDepthChart } from "./PrintDepthChart";
import { MAX_DEPTH } from "./constants";
import {
  DepthChart,
  DepthChartPlayer,
  DepthChartPlayerAssignment,
  DepthChartPosition,
  Side,
} from "../../../types/DepthChartTypes";
import { AllSideTabs } from "../../../constants";

// Helper function to transform player assignments into the format expected by components
const transformPlayerAssignments = (
  positions: DepthChartPosition[]
): Record<string, DepthChartPlayer[]> => {
  return positions.reduce<Record<string, DepthChartPlayer[]>>(
    (acc, position) => {
      if (position.player_assignments) {
        acc[position.position_id] = position.player_assignments.map(
          (assignment) => assignment.depth_chart_player
        );
      } else {
        acc[position.position_id] = [];
      }
      return acc;
    },
    {}
  );
};

export const DepthChartRoot = () => {
  const { rosterPlayers } = useRoster();
  const { selectedSeason } = useSeason();
  const { club } = useClub();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [depthChartImage, setDepthChartImage] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const depthChartRef = useRef<HTMLDivElement>(null);

  const {
    saveDepthChart,
    isLoading,
    error,
    success,
    editMode,
    setEditMode,
    selectedDepthChart,
    setSelectedDepthChart,
    selectedSide,
    handleTabChange,
    selectedTab,
    createNewFromTemplate,
    depthCharts,
  } = useDepthChart();

  const handlePrint = usePrintDepthChart(
    depthChartRef,
    selectedSide,
    selectedDepthChart
  );

  // Transform roster players into a format for the player list
  const availablePlayers = transformRosterPlayersToList(
    rosterPlayers,
    selectedSide
  );

  useEffect(() => {
    if (depthCharts.length > 0) {
      setSelectedDepthChart(depthCharts[0]);
    }
  }, [depthCharts]);

  // Remove player from depth chart
  const handleRemovePlayer = (positionId: string, playerId: number) => {
    setSelectedDepthChart((prev) => {
      if (!prev) return null;
      const position = prev.positions.find((p) => p.position_id === positionId);
      if (!position) return prev;

      const updatedAssignments = (position.player_assignments || [])
        .filter((assignment) => assignment.depth_chart_player.id !== playerId)
        .map((assignment, index) => ({
          ...assignment,
          depth_order: index + 1,
        }));

      return {
        id: prev.id,
        name: prev.name,
        side: prev.side,
        club: prev.club,
        season: prev.season,
        is_template: prev.is_template,
        positions: prev.positions.map((p) => {
          if (p.position_id === positionId) {
            return {
              ...p,
              player_assignments: updatedAssignments,
            };
          }
          return p;
        }),
        game: prev.game,
        personnel_group: prev.personnel_group,
        created_by: prev.created_by,
        created_at: prev.created_at,
      };
    });
  };

  // Drag and drop handler
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // If dropped outside a droppable area or same position
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      console.log("Drop was outside a droppable area or in the same position");
      return;
    }

    try {
      // Handle dragging from player list to a position
      if (source.droppableId === "playerList") {
        console.log("Dragging from player list to a position");
        // Get the player ID from the draggable ID
        const playerId = parseInt(draggableId.replace("player-", ""));
        const player = availablePlayers.find((p) => p.id === playerId);

        if (!player) {
          console.log("Player not found in available players");
          return;
        }

        // Parse the destination ID
        let isDepthDrop = false;
        let positionId = destination.droppableId.replace("position-", "");

        // Check if dropping directly on position box or in a depth slot
        if (destination.droppableId.startsWith("depth-")) {
          isDepthDrop = true;
          positionId = destination.droppableId.replace("depth-", "");
        }

        setSelectedDepthChart((prev) => {
          if (!prev) return null;
          console.log("prev", prev);
          // Get existing player assignments for this position
          const position = prev.positions.find(
            (p) => p.position_id === positionId
          );
          const existingAssignments = [...(position?.player_assignments || [])];

          // Transform current assignments into the format needed for validation
          const allPositionAssignments = prev.positions.reduce<
            Record<string, DepthChartPlayer[]>
          >((acc, pos) => {
            acc[pos.position_id] = (pos.player_assignments || []).map(
              (a) => a.depth_chart_player
            );
            return acc;
          }, {});

          // Get target depth (1-based index)
          const targetDepth = isDepthDrop
            ? destination.index + 1
            : existingAssignments.length + 1;

          // Check if player can be added to this position
          if (
            !isValidPositionDrop(
              positionId,
              player.id,
              existingAssignments.map((a) => a.depth_chart_player),
              allPositionAssignments,
              targetDepth
            )
          ) {
            console.log("Player cannot be added to this position");
            return prev;
          }
          console.log("player can be added to this position");

          // Create new player assignment object
          const newAssignment: DepthChartPlayerAssignment = {
            depth_order: isDepthDrop
              ? destination.index + 1
              : existingAssignments.length + 1,
            depth_chart_player: {
              id: player.id,
              name: player.name,
              jersey_number: player.jersey_number,
              status: player.status,
              position: player.position,
              position_group: player.position_group,
              player: player.player,
              roster_membership_id: player.roster_membership_id,
            },
            roster_player: {
              id: 0,
              player: { id: 0, name: "" },
              jersey_number: 0,
              status: "",
            },
          };

          // Insert at appropriate position in depth order
          const updatedAssignments = [...existingAssignments];
          if (isDepthDrop) {
            // Insert at specific depth position and update depth_order for all subsequent players
            updatedAssignments.splice(destination.index, 0, newAssignment);
            updatedAssignments.forEach((assignment, index) => {
              assignment.depth_order = index + 1;
            });
          } else {
            // Add to end of depth chart for this position
            updatedAssignments.push(newAssignment);
          }

          console.log("updatedAssignments", updatedAssignments);

          // Get max depth for this position and ensure we don't exceed it
          const trimmedAssignments = updatedAssignments.slice(0, MAX_DEPTH);
          console.log("trimmedAssignments", trimmedAssignments);

          // Update the positions array with the new player assignments
          return {
            id: prev.id,
            name: prev.name,
            side: prev.side,
            club: prev.club,
            season: prev.season,
            is_template: prev.is_template,
            positions: prev.positions.map((position) => {
              console.log("position.position_id", position.position_id);
              console.log("positionId", positionId);
              if (position.position_id === positionId) {
                console.log("position.position_id matches positionId");
                return {
                  ...position,
                  player_assignments: trimmedAssignments,
                };
              }
              return position;
            }),
            game: prev.game,
            personnel_group: prev.personnel_group,
            created_by: prev.created_by,
            created_at: prev.created_at,
          };
        });
      }

      // Handle dragging from depth chart to player list (remove player from depth chart)
      else if (
        source.droppableId.startsWith("depth-") &&
        destination.droppableId === "playerList"
      ) {
        console.log("Dragging from depth chart to player list");
        const positionId = source.droppableId.replace("depth-", "");
        // Extract player ID from the new format "depth-player-positionId-playerId"
        const playerId = parseInt(draggableId.split("-").pop() || "0");

        // Remove player from the position
        handleRemovePlayer(positionId, playerId);
      }

      // Handle reordering within a depth chart
      else if (
        source.droppableId.startsWith("depth-") &&
        source.droppableId === destination.droppableId
      ) {
        console.log("Reordering within a depth chart");
        const positionId = source.droppableId.replace("depth-", "");

        setSelectedDepthChart((prev) => {
          if (!prev) return null;
          const position = prev.positions.find(
            (p) => p.position_id === positionId
          );
          if (!position) return prev;

          const assignments = [...(position.player_assignments || [])];
          const [movedAssignment] = assignments.splice(source.index, 1);
          assignments.splice(destination.index, 0, movedAssignment);

          // Update depth_order for all assignments
          const updatedAssignments = assignments.map((assignment, index) => ({
            ...assignment,
            depth_order: index + 1,
          }));

          return {
            id: prev.id,
            name: prev.name,
            side: prev.side,
            club: prev.club,
            season: prev.season,
            is_template: prev.is_template,
            positions: prev.positions.map((p) => {
              if (p.position_id === positionId) {
                return {
                  ...p,
                  player_assignments: updatedAssignments,
                };
              }
              return p;
            }),
            game: prev.game,
            personnel_group: prev.personnel_group,
            created_by: prev.created_by,
            created_at: prev.created_at,
          };
        });
      }

      // Handle moving player between depth charts
      else if (
        source.droppableId.startsWith("depth-") &&
        destination.droppableId.startsWith("depth-")
      ) {
        console.log("Moving player between depth charts");
        const sourcePositionId = source.droppableId.replace("depth-", "");
        const destPositionId = destination.droppableId.replace("depth-", "");
        const playerId = parseInt(draggableId.replace("depth-player-", ""));

        setSelectedDepthChart((prev) => {
          if (!prev) return null;
          // Get source and destination positions
          const sourcePosition = prev.positions.find(
            (p) => p.position_id === sourcePositionId
          );
          const destPosition = prev.positions.find(
            (p) => p.position_id === destPositionId
          );
          if (!sourcePosition || !destPosition) return prev;

          // Get assignments from both positions
          const sourceAssignments = [
            ...(sourcePosition.player_assignments || []),
          ];
          const destAssignments = [...(destPosition.player_assignments || [])];

          // Find the assignment to move
          const assignmentIndex = sourceAssignments.findIndex(
            (a) => a.depth_chart_player.id === playerId
          );
          if (assignmentIndex === -1) return prev;

          // Remove from source and add to destination
          const [movedAssignment] = sourceAssignments.splice(
            assignmentIndex,
            1
          );
          destAssignments.splice(destination.index, 0, movedAssignment);

          // Update depth_order for both positions
          const updatedSourceAssignments = sourceAssignments.map(
            (assignment, index) => ({
              ...assignment,
              depth_order: index + 1,
            })
          );

          const updatedDestAssignments = destAssignments
            .slice(0, MAX_DEPTH)
            .map((assignment, index) => ({
              ...assignment,
              depth_order: index + 1,
            }));

          // Update both positions
          return {
            id: prev.id,
            name: prev.name,
            side: prev.side,
            club: prev.club,
            season: prev.season,
            is_template: prev.is_template,
            positions: prev.positions.map((p) => {
              if (p.position_id === sourcePositionId) {
                return {
                  ...p,
                  player_assignments: updatedSourceAssignments,
                };
              }
              if (p.position_id === destPositionId) {
                return {
                  ...p,
                  player_assignments: updatedDestAssignments,
                };
              }
              return p;
            }),
            game: prev.game,
            personnel_group: prev.personnel_group,
            created_by: prev.created_by,
            created_at: prev.created_at,
          };
        });
      }
    } catch (error) {
      console.error("Error in drag & drop operation:", error);
    }
  };

  const handleSelectDepthChart = (depthChart: DepthChart) => {
    setSelectedDepthChart(depthChart);
  };

  // Render the depth chart based on the current side
  const renderDepthChart = () => {
    if (!selectedDepthChart) return null;
    const positionAssignments = transformPlayerAssignments(
      selectedDepthChart.positions
    );
    return (
      <DepthChartComponent
        positionAssignments={positionAssignments}
        onRemovePlayer={handleRemovePlayer}
      />
    );
  };

  const handleCreateNew = async () => {
    if (!selectedSeason || !club) {
      console.error("No season or club selected");
      return;
    }

    try {
      // This will handle fetching template if needed and creating a new chart
      await createNewFromTemplate(selectedSide);
      setEditMode(true);
    } catch (error) {
      console.error("Error creating new depth chart:", error);
      // You might want to show an error message to the user here
    }
  };

  const generateDepthChartImage = async () => {
    if (!depthChartRef.current) return;

    try {
      setIsGeneratingImage(true);

      // Dynamically import html2canvas
      const { default: html2canvas } = await import("html2canvas");

      // Use html2canvas to convert the div to an image
      const canvas = await html2canvas(depthChartRef.current, {
        backgroundColor: "#121212",
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
      });

      // Convert canvas to data URL
      const imageDataURL = canvas.toDataURL("image/png");
      setDepthChartImage(imageDataURL);

      return imageDataURL;
    } catch (error) {
      console.error("Error generating depth chart image:", error);
      return null;
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleOpenShareModal = async () => {
    setShareModalOpen(true);

    // Generate image when modal opens
    await generateDepthChartImage();
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setDepthChartImage(null);
  };

  const handleSave = async () => {
    // Ensure we have a season and club
    if (!selectedSeason || !club) {
      console.error("No season or club selected");
      return;
    }
    await saveDepthChart();
    setEditMode(false);
  };

  // Viewport and dynamic sizing state
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [containerHeight, setContainerHeight] = useState(window.innerHeight);

  const RIGHT_SIDEBAR_WIDTH = 300;
  const TABS_HEIGHT = 64;
  const PADDING_Y = 48;

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
      setContainerHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate available area for DepthChart (excluding sidebar and paddings)
  const availableWidth = Math.max(containerWidth - RIGHT_SIDEBAR_WIDTH - 48, 0); // 48px for gap/margins

  // Maintain 8.5:11 (height:width) ratio (landscape)
  // width = (11/8.5) * height
  const RATIO = 8.5 / 11; // height / width

  // Ideal chart height (can be taller than viewport for more width)
  const IDEAL_CHART_HEIGHT = Math.floor(containerHeight * 1.5);
  let chartHeight = IDEAL_CHART_HEIGHT;
  let chartWidth = chartHeight / RATIO;

  // Ensure chart never covers sidebar
  if (chartWidth > availableWidth) {
    chartWidth = availableWidth;
    chartHeight = chartWidth * RATIO;
  }


  return (
    <Box
      sx={{
        p: 1,
        maxWidth: "100vw",
        width: "100%",
        bgcolor: "#121212",
        color: "white",
        height: "100vh",
        boxSizing: "border-box",
        overflow: "hidden", // Prevent scrollbars
      }}
    >
      {/* Tabs for chart types */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) =>
            handleTabChange(AllSideTabs[newValue].value)
          }
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "rgba(123, 45, 58, 0.9)",
            },
            "& .MuiTab-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-selected": {
                color: "white",
              },
            },
          }}
        >
          {AllSideTabs.map((tab, index) => (
            <Tab
              key={tab.value}
              label={tab.label}
              id={`depth-chart-tab-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <Typography variant="h6" sx={{ color: "white" }}>
            Loading...
          </Typography>
        </Box>
      )}

      {/* Main content area with depth chart and right sidebar */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: 2,
            height: `calc(100vh - ${TABS_HEIGHT + PADDING_Y}px)`,
            overflow: "hidden",
          }}
        >
          {/* Depth Chart (dynamic size, centered vertically, scrollable if needed) */}
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              maxHeight: "100%",
              overflowY: chartHeight >= IDEAL_CHART_HEIGHT ? "auto" : "visible",
              width: chartWidth,
            }}
          >
            <Box
              ref={depthChartRef}
              sx={{
                width: chartWidth,
                height: chartHeight,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                bgcolor: "#181818",
                borderRadius: 2,
                boxShadow: 3,
                justifyContent: "center",
                alignItems: "center",
                overflow: "visible",
                transition: "width 0.2s, height 0.2s",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {renderDepthChart()}
              </Box>
            </Box>
          </Box>
          {/* Right sidebar with selector and player list (fixed width) */}
          <Box
            sx={{
              width: RIGHT_SIDEBAR_WIDTH,
              minWidth: RIGHT_SIDEBAR_WIDTH,
              maxWidth: RIGHT_SIDEBAR_WIDTH,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: "space-evenly" }}
            >
              {editMode ? (
                <>
                  {/* Close Button */}
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={() => setEditMode(false)}
                    size="small"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.23)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    Close
                  </Button>
                  {/* Save Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.23)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  {/* Edit Button */}
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    size="small"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.23)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    Edit
                  </Button>
                  {/* Print Button */}
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    size="small"
                    onClick={handlePrint}
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.23)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    Print
                  </Button>
                  {/* Share Button */}
                  <Button
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    size="small"
                    onClick={handleOpenShareModal}
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.23)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    Share
                  </Button>
                </>
              )}
            </Stack>

            {/* Player List or Saved Charts */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                width: "100%",
              }}
            >
              {editMode ? (
                <PlayerList
                  players={availablePlayers}
                  selectedSide={selectedSide}
                />
              ) : (
                <SavedChartsList
                  isLoading={isLoading}
                  error={error}
                  onCreateNew={handleCreateNew}
                  onSelectDepthChart={handleSelectDepthChart}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DragDropContext>

      {/* Share Modal */}
      {selectedDepthChart && (
        <ShareModal
          open={shareModalOpen}
          onClose={handleCloseShareModal}
          depthChartImage={depthChartImage}
          isGeneratingImage={isGeneratingImage}
          selectedDepthChart={selectedDepthChart}
        />
      )}
    </Box>
  );
};
